import { useEffect, type FC } from 'react';

import { MARKDOWN_SUFFIX } from './markdownSuffix.ts';

export type MarkdownSuffixSyncProps = {
	/** Current value of the field that may end with `.md`. */
	fieldValue: string;
	/** Field setter — typically `fieldProps.onChange` from `@atlaskit/form`. */
	fieldOnChange: (value: string) => void;
	isMarkdownMode: boolean;
	/**
	 * Owned by the gesture parent. The parent sets `.current` to the
	 * stripped value (e.g. `Hello World`) when the user appends `.md` to
	 * trigger the convert-to-markdown flow. Once `isMarkdownMode` flips
	 * true, this component drops the typed `.md` from the field's stored
	 * value (one-shot) and clears the ref.
	 */
	pendingStripValueRef: React.MutableRefObject<string | null>;
};

/**
 * Render-only effect runner. Lives inside the `<Field>` render-prop so
 * it has access to `fieldProps.onChange`. When the markdown-mode flag
 * flips true and a pending strip is queued by the gesture parent, drop
 * the typed `.md` from the field's stored value (one-shot).
 */
export const MarkdownSuffixSync: FC<MarkdownSuffixSyncProps> = ({
	fieldValue,
	fieldOnChange,
	isMarkdownMode,
	pendingStripValueRef,
}) => {
	useEffect(() => {
		const pending = pendingStripValueRef.current;
		if (
			isMarkdownMode &&
			pending != null &&
			// Only apply if the field hasn't been touched since the gesture —
			// otherwise we'd clobber whatever the user typed afterwards.
			fieldValue === `${pending}${MARKDOWN_SUFFIX}`
		) {
			fieldOnChange(pending);
			pendingStripValueRef.current = null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMarkdownMode, fieldValue, fieldOnChange]);
	return null;
};
